import React, { useState } from 'react';
import {
  Box, Button, Divider, Flex, Heading, HStack, IconButton, Input, InputGroup, InputLeftElement,
  InputRightElement, SimpleGrid, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { FaArrowsAltV } from 'react-icons/fa';
import {
  RnwyCfg, RnwyCatalogItem, RnwyCategory, RnwyEquipmentCategory, RnwySwitchPilotCfg,
  emptySwitchPilotCfg, markupMoney, markupPct, brandPool,
} from './forms/rnwyCfg';
import {
  MODULE_ORDER, DEFAULT_MODULE_LABELS, categoryKeyOf, orderedCategories, normalizedCategories,
} from './forms/rnwyScopeBuilder';

// item 647 — read-only "actual/marked-up" value shown beside a base rate.
const fmtMoney = (n: number): string => `$${(n || 0).toFixed(2)}`;
const fmtPct = (n: number): string => `${((n || 0) * 100).toFixed(1)}%`;

// RNWY 2.0 · P1 — the CFG rate-card editor (Settings → RNWY). Controlled: it renders the parsed
// RnwyCfg and calls onChange with the next object. Percentages are shown ×100 (store ÷100);
// multipliers + fractions are shown raw with a clear label. The pricing engine (P3) reads all of this.

const num = (v: string): number => (v === '' ? 0 : Number(v));

const Money: React.FC<{ value: number; onChange: (v: number) => void; 'aria-label': string }> = ({
  value, onChange, 'aria-label': ariaLabel,
}) => (
  <InputGroup size="sm">
    <InputLeftElement pointerEvents="none" color="gray.400">$</InputLeftElement>
    <Input
      pl={7}
      type="number"
      step="0.01"
      min={0}
      value={value ?? 0}
      aria-label={ariaLabel}
      onChange={(e) => onChange(num(e.target.value))}
    />
  </InputGroup>
);

const Pct: React.FC<{ value: number; onChange: (v: number) => void; 'aria-label': string }> = ({
  value, onChange, 'aria-label': ariaLabel,
}) => (
  <InputGroup size="sm">
    <Input
      pr={7}
      type="number"
      step="0.1"
      min={0}
      value={Math.round((value ?? 0) * 1000) / 10}
      aria-label={ariaLabel}
      onChange={(e) => onChange(num(e.target.value) / 100)}
    />
    <InputRightElement pointerEvents="none" color="gray.400">%</InputRightElement>
  </InputGroup>
);

const Raw: React.FC<{ value: number; step?: string; onChange: (v: number) => void; 'aria-label': string }> = ({
  value, step, onChange, 'aria-label': ariaLabel,
}) => (
  <Input
    size="sm"
    type="number"
    step={step ?? '0.01'}
    min={0}
    value={value ?? 0}
    aria-label={ariaLabel}
    onChange={(e) => onChange(num(e.target.value))}
  />
);
Raw.defaultProps = { step: '0.01' };

const Labeled: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    {/* dart (2026-07-16, Chad) — reserve a 2-line height so labels that wrap don't push their input
        down out of alignment with the rest of the grid row */}
    <Text fontSize="xs" mb={1} color="gray.500" lineHeight="1.25" minH="2.4em" display="flex" alignItems="flex-end">{label}</Text>
    {children}
  </Box>
);

const RnwyCfgEditor: React.FC<{ value: RnwyCfg; onChange: (cfg: RnwyCfg) => void }> = ({ value, onChange }) => {
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const set = (patch: Partial<RnwyCfg>) => onChange({ ...value, ...patch });
  const setCatalog = (i: number, patch: Partial<RnwyCatalogItem>) => set({
    catalog: value.catalog.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
  });
  const sp: RnwySwitchPilotCfg = value.switchPilotCfg ?? emptySwitchPilotCfg();
  const setSp = (patch: Partial<RnwySwitchPilotCfg>) => set({ switchPilotCfg: { ...sp, ...patch } });

  // dart (2026-07-16, Chad) — drag catalog items to REORDER and to REPARENT into a category. The
  // catalog is rendered grouped by category (header rows double as drop zones). Hand-rolled native
  // HTML5 DnD (no dnd lib — CRA4/webpack4/Node16), mirroring the OrderableSections grab-handle
  // pattern: a row is `draggable` ONLY after its grip is pressed, so the label/price inputs stay
  // editable. Dropping ON A ROW places the item before it and adopts that row's category; dropping
  // ON A CATEGORY HEADER moves it into that category. The catalog array + each item's `category` are
  // CFG data (ride the per-proposal snapshot), so both order and grouping persist through onChange.
  const [armedIdx, setArmedIdx] = useState<number | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [overCat, setOverCat] = useState<string | null>(null);
  // category reordering (drag a category header's grip) — separate from item dragging
  const [armedCatKey, setArmedCatKey] = useState<string | null>(null);
  const [catDragKey, setCatDragKey] = useState<string | null>(null);
  const [catOverKey, setCatOverKey] = useState<string | null>(null);
  const dropBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const catHoverBg = useColorModeValue('blue.50', 'whiteAlpha.300');
  const gripHoverBg = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const catHeaderBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const catHeaderColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const clearDrag = () => {
    setArmedIdx(null); setDragIdx(null); setOverIdx(null); setOverCat(null);
    setArmedCatKey(null); setCatDragKey(null); setCatOverKey(null);
  };
  // reparent `from` into `categoryKey`; place before `beforeIndex` (an original-array index) or, when
  // null (header drop), append so it becomes the last sibling in that category.
  const applyDrop = (from: number, categoryKey: string, beforeIndex: number | null) => {
    if (from < 0) return;
    const next = [...value.catalog];
    const [item] = next.splice(from, 1);
    const moved: RnwyCatalogItem = { ...item, categoryKey, category: undefined };
    if (beforeIndex === null) {
      next.push(moved);
    } else {
      next.splice(from < beforeIndex ? beforeIndex - 1 : beforeIndex, 0, moved);
    }
    set({ catalog: next });
  };

  // dart (2026-07-16, Chad) — categories are editable data (stable key + display name). `cats` seeds
  // from DEFAULT_CATEGORIES so an old CFG is editable immediately; the first edit materializes
  // value.categories. Renaming touches ONLY the name — items link by the stable key, so a rename never
  // moves or strands them (this fixes the earlier name-cascade bug). `ocats` adds the uncategorized bucket.
  const cats: RnwyCategory[] = normalizedCategories(value); // key-normalized (heals legacy keyless data)
  const ocats = orderedCategories(value);
  const setCategories = (nextCats: RnwyCategory[]) => set({ categories: nextCats });
  const renameCategory = (idx: number, newName: string) => setCategories(
    cats.map((c, i) => (i === idx ? { ...c, name: newName } : c)),
  );
  const addCategory = () => setCategories([...cats, { key: `cat_${Date.now().toString(36)}`, name: 'New category' }]);
  // reorder categories: move `fromKey` to sit where `toKey` is (drag a category header's grip)
  const moveCategory = (fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    const from = cats.findIndex((c) => c.key === fromKey);
    const to = cats.findIndex((c) => c.key === toKey);
    if (from < 0 || to < 0) return;
    const list = [...cats];
    const [m] = list.splice(from, 1);
    list.splice(to, 0, m);
    setCategories(list);
  };

  // item 681 (Chad) — the "Sources" pool (individual controllable sources), the repurposed old Brands
  // list. Seeds from value.sources, else the old brand pool (so nothing typed under Brands is lost).
  // Editing writes value.sources. Each venue on a quote multi-selects from this into its scope.
  const pool: string[] = value.sources && value.sources.length ? value.sources : brandPool(value);
  const setPool = (next: string[]) => set({ sources: next });
  const setSourceAt = (i: number, name: string) => setPool(pool.map((b, idx) => (idx === i ? name : b)));
  const addPoolSource = () => setPool([...pool, '']);
  const removePoolSource = (i: number) => setPool(pool.filter((_, idx) => idx !== i));

  // item 681(E) (Chad) — the categorized "equipment we control" list (category → products).
  const equip: RnwyEquipmentCategory[] = value.controlledEquipment ?? [];
  const setEquip = (next: RnwyEquipmentCategory[]) => set({ controlledEquipment: next });
  const addEquipCat = () => setEquip([...equip, { name: 'New category', items: [] }]);
  const removeEquipCat = (ci: number) => setEquip(equip.filter((_, i) => i !== ci));
  const renameEquipCat = (ci: number, name: string) => setEquip(equip.map((c, i) => (i === ci ? { ...c, name } : c)));
  const addEquipItem = (ci: number) => setEquip(equip.map((c, i) => (i === ci ? { ...c, items: [...c.items, ''] } : c)));
  const setEquipItem = (ci: number, ii: number, name: string) => setEquip(
    equip.map((c, i) => (i === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? name : it)) } : c)),
  );
  const removeEquipItem = (ci: number, ii: number) => setEquip(
    equip.map((c, i) => (i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c)),
  );

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" p={4}>
      <Heading size="xs" mb={1}>CFG — RNWY pricing rate card</Heading>
      <Text fontSize="xs" color="gray.500" mb={3}>
        The Schedule B pricing brain. Seeded from the 6-10-2026 sheet — tune it here; the RNWY
        pricing engine reads these. Company-level, never edited on a proposal.
      </Text>

      {/* dart (2026-07-15, Chad) — nomenclature: COST (our base numbers) → these multipliers →
          GROUP PRICE (what Pulse Group charges the integrator / group member). Separately, the
          Suggested-retail markup takes Group price → the integrator's Suggested retail to their client. */}
      <Text fontSize="sm" fontWeight="semibold" mb={2}>Global multipliers (cost → group price)</Text>
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
        <Labeled label="Config increase (×)"><Raw value={value.configIncrease} aria-label="config increase" onChange={(v) => set({ configIncrease: v })} /></Labeled>
        <Labeled label="Perpetual increase (×)"><Raw value={value.perpetualIncrease} aria-label="perpetual increase" onChange={(v) => set({ perpetualIncrease: v })} /></Labeled>
        <Labeled label="Subscription increase (×)"><Raw value={value.subscriptionIncrease} aria-label="subscription increase" onChange={(v) => set({ subscriptionIncrease: v })} /></Labeled>
        <Labeled label="Help-desk first-venue (×)"><Raw value={value.helpDeskFirstIncrease} aria-label="help desk first increase" onChange={(v) => set({ helpDeskFirstIncrease: v })} /></Labeled>
        <Labeled label="Help-desk add-on (×)"><Raw value={value.helpDeskAddOnIncrease} aria-label="help desk addon increase" onChange={(v) => set({ helpDeskAddOnIncrease: v })} /></Labeled>
      </SimpleGrid>

      <Text fontSize="sm" fontWeight="semibold" mb={2}>Subscription terms</Text>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        <Labeled label="Monthly license floor ($)"><Money value={value.monthlyLicenseFloor} aria-label="monthly license floor" onChange={(v) => set({ monthlyLicenseFloor: v })} /></Labeled>
        <Labeled label="2-Yr config discount (%)"><Pct value={value.twoYearConfigDiscount} aria-label="2yr config discount" onChange={(v) => set({ twoYearConfigDiscount: v })} /></Labeled>
        <Labeled label="5-Yr config discount (%)"><Pct value={value.fiveYearConfigDiscount} aria-label="5yr config discount" onChange={(v) => set({ fiveYearConfigDiscount: v })} /></Labeled>
        <Labeled label="Perpetual buy-in (months of subscription)"><Raw value={value.buyInMonths} step="1" aria-label="buy-in months" onChange={(v) => set({ buyInMonths: v })} /></Labeled>
        {/* item 662 — global default for the client-facing suggested-resale markup (× on group price).
            0 / 1 = off. Overridable per proposal on Step 3. */}
        <Labeled label="Suggested-retail markup (× group → client)"><Raw value={value.suggestedResaleMarkup} aria-label="suggested retail markup" onChange={(v) => set({ suggestedResaleMarkup: v })} /></Labeled>
      </SimpleGrid>

      <Divider my={3} />
      <Text fontSize="sm" fontWeight="semibold" mb={2}>System sizes (base config + perpetual %)</Text>
      <Box overflowX="auto" mb={4}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>System size</Th>
              <Th isNumeric>Cost</Th>
              <Th isNumeric>Group price</Th>
              <Th isNumeric>Perpetual %</Th>
              <Th isNumeric>Group %</Th>
            </Tr>
          </Thead>
          <Tbody>
            {value.systemSizes.map((s, i) => (
              <Tr key={s.key}>
                {/* dart (2026-07-15, Chad) — the system-size NAME is now editable; it prints in the SOW
                    venue heading ("## Venue — {label} system"). */}
                <Td><Input size="sm" value={s.label} aria-label={`${s.key} label`} onChange={(e) => set({ systemSizes: value.systemSizes.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)) })} /></Td>
                <Td isNumeric><Money value={s.base} aria-label={`${s.key} base`} onChange={(v) => set({ systemSizes: value.systemSizes.map((x, idx) => (idx === i ? { ...x, base: v } : x)) })} /></Td>
                {/* item 647 — base config × Config increase */}
                <Td isNumeric><Text fontSize="sm" color="gray.500">{fmtMoney(markupMoney(s.base, value.configIncrease))}</Text></Td>
                <Td isNumeric><Pct value={s.perpetualPct} aria-label={`${s.key} perpetual pct`} onChange={(v) => set({ systemSizes: value.systemSizes.map((x, idx) => (idx === i ? { ...x, perpetualPct: v } : x)) })} /></Td>
                {/* item 647 — perpetual % × Perpetual increase */}
                <Td isNumeric><Text fontSize="sm" color="gray.500">{fmtPct(markupPct(s.perpetualPct, value.perpetualIncrease))}</Text></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Divider my={3} />
      {/* dart (2026-07-15, Chad) — editable module NAMES that print in the SOW "System scope" line.
          Blank = fall back to the built-in default (shown as the caption + placeholder). */}
      <Text fontSize="sm" fontWeight="semibold" mb={1}>Module names (printed in the scope)</Text>
      <Text fontSize="xs" color="gray.500" mb={2}>
        The names shown in the generated SOW “System scope — Included / Excluded” line. Leave blank to
        use the default.
      </Text>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        {MODULE_ORDER.map((k) => (
          <Labeled key={k} label={DEFAULT_MODULE_LABELS[k]}>
            <Input
              size="sm"
              value={value.moduleLabels?.[k] ?? ''}
              placeholder={DEFAULT_MODULE_LABELS[k]}
              aria-label={`${k} module label`}
              onChange={(e) => set({ moduleLabels: { ...(value.moduleLabels ?? {}), [k]: e.target.value } })}
            />
          </Labeled>
        ))}
      </SimpleGrid>

      <Divider my={3} />
      <Text fontSize="sm" fontWeight="semibold" mb={2}>Help Desk (monthly)</Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        {(['firstVenue', 'addOnVenue'] as const).map((scope) => {
          // item 647 — actual monthly = base × the scope's multiplier (first-venue vs add-on)
          const mult = scope === 'firstVenue' ? value.helpDeskFirstIncrease : value.helpDeskAddOnIncrease;
          return (
            <Box key={scope}>
              <Text fontSize="xs" color="gray.500" mb={1}>{scope === 'firstVenue' ? 'First venue' : 'Each add-on venue'}</Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                {(['gold', 'platinum', 'titanium'] as const).map((tier) => (
                  <Labeled key={tier} label={tier[0].toUpperCase() + tier.slice(1)}>
                    <Money
                      value={value.helpDesk[scope][tier]}
                      aria-label={`${scope} ${tier}`}
                      onChange={(v) => set({ helpDesk: { ...value.helpDesk, [scope]: { ...value.helpDesk[scope], [tier]: v } } })}
                    />
                    <Text fontSize="xx-small" color="gray.500" mt={1}>{`${fmtMoney(markupMoney(value.helpDesk[scope][tier], mult))}/mo`}</Text>
                  </Labeled>
                ))}
              </SimpleGrid>
            </Box>
          );
        })}
      </SimpleGrid>

      <Divider my={3} />
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight="semibold">Device / element catalog (base prices)</Text>
        <IconButton
          size="xs"
          aria-label="Add catalog item"
          icon={<FiPlus />}
          onClick={() => set({ catalog: [...value.catalog, { key: `custom_${value.catalog.length}`, label: '', base: 0 }] })}
        />
      </HStack>
      {/* dart (2026-07-16, Chad) — drag hint */}
      <Text fontSize="xs" color="gray.500" mb={2}>
        Grouped by category. Drag a row’s grip to reorder an item, or drop it on a category header to
        move it there. Drag a category header’s grip to reorder the categories themselves. Rename any
        category (including “Additional systems”) in its header; a new item starts under “Additional
        systems” until you move it.
      </Text>
      <Box overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th w="28px" p={0} />
              {/* item 1183 dart#5 (Chad, mobile) — a real minimum: without it the table
                  CRUSHED the item/cost inputs to ~1ch at phone width instead of letting
                  the overflowX wrapper scroll */}
              <Th minW="11rem">Item</Th>
              <Th isNumeric w="140px" minW="120px">Cost</Th>
              <Th isNumeric w="120px">Group price</Th>
              <Th w="40px" />
            </Tr>
          </Thead>
          <Tbody>
            {ocats.map((cat, ci) => {
              const catIdx = cats.findIndex((c) => c.key === cat.key);
              const editable = catIdx >= 0; // now true for every rendered category, incl. the bucket
              const rows = value.catalog
                .map((c, i) => ({ c, i }))
                .filter(({ c }) => categoryKeyOf(c, ocats) === cat.key);
              const isCatTarget = overCat === cat.key && dragIdx !== null; // item being dropped here
              const isCatMoveTarget = catOverKey === cat.key && catDragKey !== null && catDragKey !== cat.key;
              return (
                // STABLE key = the category's key (never changes on rename), so typing a name never
                // remounts the row / steals focus, and items (linked by key) are never disturbed.
                <React.Fragment key={cat.key}>
                  {/* dart (2026-07-16, Chad) — breathing room between category groups so each (esp. the
                      project-level one) reads as its own block and stays anchored in its spot */}
                  {ci > 0 && (
                    <Tr aria-hidden="true">
                      <Td colSpan={5} h="14px" p={0} border="none" />
                    </Tr>
                  )}
                  <Tr
                    draggable={armedCatKey === cat.key}
                    onDragStart={(e) => { if (armedCatKey !== cat.key) return; e.dataTransfer.effectAllowed = 'move'; setCatDragKey(cat.key); }}
                    onDragOver={(e) => {
                      if (catDragKey !== null && catDragKey !== cat.key) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (catOverKey !== cat.key) setCatOverKey(cat.key); return; }
                      if (dragIdx !== null) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overCat !== cat.key) { setOverCat(cat.key); setOverIdx(null); } }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (catDragKey !== null) moveCategory(catDragKey, cat.key);
                      else if (dragIdx !== null) applyDrop(dragIdx, cat.key, null);
                      clearDrag();
                    }}
                    onDragEnd={clearDrag}
                    opacity={catDragKey === cat.key ? 0.4 : 1}
                    bg={isCatTarget || isCatMoveTarget ? catHoverBg : catHeaderBg}
                  >
                    <Td colSpan={5} py={2} borderBottomWidth="0">
                      <Flex align="center" sx={{ gap: '8px' }} wrap="wrap">
                        {/* category reorder grip — press to arm, then drag the header up/down */}
                        <Box
                          as="button"
                          type="button"
                          aria-label={`Drag to reorder category ${cat.name}`}
                          title="Drag to reorder category"
                          onMouseDown={() => setArmedCatKey(cat.key)}
                          onMouseUp={() => setArmedCatKey(null)}
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          p={1}
                          borderRadius="md"
                          color="gray.400"
                          cursor={armedCatKey === cat.key ? 'grabbing' : 'grab'}
                          _hover={{ bg: gripHoverBg, color: 'gray.600' }}
                        >
                          <Box as={FaArrowsAltV} fontSize="12px" />
                        </Box>
                        {editable ? (
                          <Input size="xs" maxW="260px" fontWeight="bold" value={cats[catIdx].name} aria-label={`category ${catIdx} name`} onChange={(e) => renameCategory(catIdx, e.target.value)} />
                        ) : (
                          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.06em" color={catHeaderColor}>{cat.name}</Text>
                        )}
                        <Text fontSize="xs" opacity={0.55}>{`(${rows.length})`}</Text>
                      </Flex>
                    </Td>
                  </Tr>
                  {rows.map(({ c, i }) => (
                    <Tr
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${c.key}-${i}`}
                      draggable={armedIdx === i}
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(i); }}
                      onDragOver={(e) => { if (dragIdx === null || dragIdx === i) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIdx !== i) { setOverIdx(i); setOverCat(null); } }}
                      onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) applyDrop(dragIdx, categoryKeyOf(c, ocats), i); clearDrag(); }}
                      onDragEnd={clearDrag}
                      opacity={dragIdx === i ? 0.4 : 1}
                      bg={overIdx === i && dragIdx !== null && dragIdx !== i ? dropBg : undefined}
                      transition="opacity 0.12s"
                    >
                      <Td w="28px" p={0} textAlign="center">
                        <Box
                          as="button"
                          type="button"
                          aria-label={`Drag to reorder or recategorize ${c.label || 'item'}`}
                          title="Drag to reorder or recategorize"
                          onMouseDown={() => setArmedIdx(i)}
                          onMouseUp={() => setArmedIdx(null)}
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          p={1}
                          borderRadius="md"
                          color="gray.400"
                          cursor={armedIdx === i ? 'grabbing' : 'grab'}
                          _hover={{ bg: gripHoverBg, color: 'gray.600' }}
                        >
                          <Box as={FaArrowsAltV} fontSize="13px" />
                        </Box>
                      </Td>
                      <Td><Input size="sm" value={c.label} aria-label={`${c.key} label`} onChange={(e) => setCatalog(i, { label: e.target.value })} /></Td>
                      <Td isNumeric><Money value={c.base} aria-label={`${c.key} base`} onChange={(v) => setCatalog(i, { base: v })} /></Td>
                      {/* item 647 — base × Config increase (the engine's catalog markup) */}
                      <Td isNumeric><Text fontSize="sm" color="gray.500">{fmtMoney(markupMoney(c.base, value.configIncrease))}</Text></Td>
                      <Td><IconButton size="xs" variant="ghost" aria-label="Remove item" icon={<FiTrash2 />} onClick={() => set({ catalog: value.catalog.filter((_, idx) => idx !== i) })} /></Td>
                    </Tr>
                  ))}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      {/* dart (2026-07-16, Chad) — add a new category (rename it in its header) */}
      <Button size="xs" mt={2} variant="outline" leftIcon={<FiPlus />} onClick={addCategory}>Add category</Button>

      <Divider my={3} />
      {/* item 681 (Chad) — "Sources" (individual controllable sources included at a venue), the
          repurposed old Brands list. On a quote, each venue multi-selects from this into its scope. */}
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight="semibold">Sources</Text>
        <IconButton size="xs" aria-label="Add source" icon={<FiPlus />} onClick={addPoolSource} />
      </HStack>
      <Text fontSize="xs" color="gray.500" mb={2}>
        Individual controllable sources (e.g. Apple TV, cable box, laptop input, camera). On a quote,
        each venue checks the sources it includes and they auto-write into that venue’s scope.
      </Text>
      {pool.length === 0 ? (
        <Text fontSize="xs" color="gray.500" fontStyle="italic">No sources yet — click + to add your first.</Text>
      ) : (
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
          {pool.map((b, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <HStack key={i} spacing={1}>
              <Input size="sm" value={b} placeholder="Source name" aria-label={`source ${i}`} onChange={(e) => setSourceAt(i, e.target.value)} />
              <IconButton size="xs" variant="ghost" aria-label="Remove source" icon={<FiTrash2 />} onClick={() => removePoolSource(i)} />
            </HStack>
          ))}
        </SimpleGrid>
      )}

      <Divider my={3} />
      {/* item 681(E) (Chad) — categorized "equipment we control" list; each venue on a quote checks
          the products it has, which auto-write into that venue's scope. */}
      <HStack justify="space-between" mb={1}>
        <Text fontSize="sm" fontWeight="semibold">Equipment we control with RNWY</Text>
        <IconButton size="xs" aria-label="Add equipment category" icon={<FiPlus />} onClick={addEquipCat} />
      </HStack>
      <Text fontSize="xs" color="gray.500" mb={2}>
        Categorized list of the equipment RNWY controls (e.g. Video distribution → AV Pro Edge). On a
        quote, each venue checks the products it has and they auto-write into that venue’s scope.
      </Text>
      {equip.length === 0 ? (
        <Text fontSize="xs" color="gray.500" fontStyle="italic">No equipment categories yet — click + to add one.</Text>
      ) : (
        <SimpleGrid columns={1} spacing={3}>
          {equip.map((c, ci) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={ci} borderWidth="1px" borderColor={border} borderRadius="md" p={2}>
              <HStack mb={1}>
                <Input size="sm" fontWeight="semibold" value={c.name} placeholder="Category (e.g. Video distribution)" aria-label={`equipment category ${ci}`} onChange={(e) => renameEquipCat(ci, e.target.value)} />
                <IconButton size="xs" aria-label="Add product" icon={<FiPlus />} onClick={() => addEquipItem(ci)} />
                <IconButton size="xs" variant="ghost" aria-label="Remove category" icon={<FiTrash2 />} onClick={() => removeEquipCat(ci)} />
              </HStack>
              {c.items.length === 0 ? (
                <Text fontSize="xs" color="gray.500" fontStyle="italic" pl={1}>No products — click + to add one.</Text>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                  {c.items.map((it, ii) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <HStack key={ii} spacing={1}>
                      <Input size="sm" value={it} placeholder="Product / model (e.g. AV Pro Edge)" aria-label={`equipment ${ci} item ${ii}`} onChange={(e) => setEquipItem(ci, ii, e.target.value)} />
                      <IconButton size="xs" variant="ghost" aria-label="Remove product" icon={<FiTrash2 />} onClick={() => removeEquipItem(ci, ii)} />
                    </HStack>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          ))}
        </SimpleGrid>
      )}

      <Divider my={3} />
      <Text fontSize="sm" fontWeight="semibold" mb={1}>SwitchPilot module (subscription-only)</Text>
      <Text fontSize="xs" color="gray.500" mb={2}>
        One-time config = System Init + $/switch × every switch. Annual = base (covers the first 10) + a
        per-switch tier chosen by TOTAL switch count, applied to the switches over 10. Then the two markups.
      </Text>
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mb={2}>
        <Labeled label="Init switch config ($, one-time)"><Money value={sp.initConfigUnit} aria-label="sp init config" onChange={(v) => setSp({ initConfigUnit: v })} /></Labeled>
        <Labeled label="Per-switch config ($, one-time)"><Money value={sp.perSwitchConfigUnit} aria-label="sp per switch config" onChange={(v) => setSp({ perSwitchConfigUnit: v })} /></Labeled>
        <Labeled label="Base license — covers 10 ($/yr)"><Money value={sp.baseLicenseUnit} aria-label="sp base license" onChange={(v) => setSp({ baseLicenseUnit: v })} /></Labeled>
        <Labeled label="Tier 1 — ≤49 ($/yr/switch)"><Money value={sp.tier1LicenseUnit} aria-label="sp tier1" onChange={(v) => setSp({ tier1LicenseUnit: v })} /></Labeled>
        <Labeled label="Tier 2 — 50–100 ($/yr/switch)"><Money value={sp.tier2LicenseUnit} aria-label="sp tier2" onChange={(v) => setSp({ tier2LicenseUnit: v })} /></Labeled>
        <Labeled label="Tier 3 — 101–150 ($/yr/switch)"><Money value={sp.tier3LicenseUnit} aria-label="sp tier3" onChange={(v) => setSp({ tier3LicenseUnit: v })} /></Labeled>
        <Labeled label="Tier 4 — 151–1000 ($/yr/switch)"><Money value={sp.tier4LicenseUnit} aria-label="sp tier4" onChange={(v) => setSp({ tier4LicenseUnit: v })} /></Labeled>
        <Labeled label="Config increase (×)"><Raw value={sp.configIncrease} aria-label="sp config increase" onChange={(v) => setSp({ configIncrease: v })} /></Labeled>
        <Labeled label="Subscription increase (×)"><Raw value={sp.subscriptionIncrease} aria-label="sp subscription increase" onChange={(v) => setSp({ subscriptionIncrease: v })} /></Labeled>
        <Labeled label="Monthly minimum ($/mo)"><Money value={sp.monthlyFloor} aria-label="sp monthly floor" onChange={(v) => setSp({ monthlyFloor: v })} /></Labeled>
      </SimpleGrid>
    </Box>
  );
};

export default RnwyCfgEditor;
